/* eslint-disable unicorn/consistent-function-scoping */
/* eslint-disable unicorn/prevent-abbreviations */
/* eslint-disable sort-exports/sort-exports */
/* eslint-disable import/order */
/* eslint-disable import/no-internal-modules */
/* eslint-disable jsdoc/require-jsdoc */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import type { Proxy } from '#~/agent/types.js';
import { toHexFromId, toIdFromHex } from '#~/api/utils.js';
import type { T_Maybe } from '@tryiris-ai/js-types';
import type { LiveKitImpl } from '#~/livekit/livekit.js';
import { TrackingDetector } from './detect.js';
import type { Track as MegaTrack } from '#~/livekit/types.js';
import { pingSubjectForWorker } from '#~/auto-tracking/provider/utils.js';
import type { Subscription } from 'nats.ws';
import type { Agent } from '@studio/models/agent';
import { Status } from '@tryiris-ai/common.protojs/autotracking/v2/state';
import type { Device } from '@studio/models/device';
import { generateId } from '@studio/models';
import type { NewtsConnection } from '#~/newts/connection.js';
import { locateLiveKit } from '#~/livekit/utils.js';

export const workers = new Map<string, AutoTrackingWorker>();

export class AutoTrackingWorker {
  #proxy: Proxy;
  #workerId: Uint8Array;

  #status: Status;
  #deviceId: T_Maybe<Uint8Array>;

  #detector: T_Maybe<TrackingDetector>;
  #pingLoop: NodeJS.Timeout | undefined;
  #pingSubscription: T_Maybe<Subscription>;
  #subject: string = '';

  terminated: boolean = false;

  constructor(proxy: Proxy) {
    this.#workerId = toIdFromHex(generateId())!;

    this.#proxy = proxy;
    this.#status = Status.STATUS_STARTING;

    if (workers.has(toHexFromId(this.#workerId)!)) {
      return workers.get(toHexFromId(this.#workerId)!)!;
    }

    void this.#init();
  }

  get id() {
    return this.#workerId;
  }

  get activeDeviceId() {
    return this.#deviceId;
  }

  get activeAgentId() {
    return this.#agentId;
  }

  #agentId: T_Maybe<Agent['id']>;
  #embeddingBeaconSubject: T_Maybe<string>;

  async start(
    deviceId: Uint8Array,
    agentId: Agent['id'],
    track: MegaTrack,
    livekit: LiveKitImpl,
    subject: string,
    embeddingSubject: string
  ) {
    if (this.#status === Status.STATUS_STARTING) {
      return;
    }

    if (this.#status === Status.STATUS_RUNNING) {
      if (deviceId == this.#deviceId) {
        return;
      }

      this.reinit();
    }

    this.#status = Status.STATUS_STARTING;

    this.#deviceId = deviceId;
    this.#agentId = agentId;

    this.#embeddingBeaconSubject = embeddingSubject;

    // @ts-ignore
    window.worker = this;

    await this.#detector!.init(livekit, track);

    this.#status = Status.STATUS_RUNNING;

    this.#subject = subject;

    this.#detector!.detectVideo(
      track,
      track.mediaStreamTrack,
      subject,
      agentId,
      toHexFromId(this.#workerId)!,
      toHexFromId(deviceId)!,
      this.#embeddingBeaconSubject
    );

    void this.#syncState();
  }

  get deviceIDHex() {
    return toHexFromId<Device['id']>(this.#deviceId)!;
  }

  shutdown() {
    this.terminated = true;

    clearTimeout(this.#pingLoop);
    this.#detector!.releaseTracker();
    this.#detector = undefined;

    workers.delete(toHexFromId(this.#workerId)!);
  }

  async #init() {
    this.#detector = new TrackingDetector();

    this.terminated = false;
    this.#status = Status.STATUS_IDLE;

    this.#startPingListener();

    await this.#syncState(5);
    this.#startPingLoop();

    workers.set(toHexFromId(this.#workerId)!, this);
  }

  async #syncState(retry = 5) {
    if (this.terminated) {
      return;
    }

    try {
      await this.#proxy.mutate(
        {
          control: {
            studio: {
              autoTrackingWorker: {
                [toHexFromId(this.#workerId)!]: {
                  state: {
                    status: {
                      lastPing: Date.now(),
                      status: this.#status,
                      readyAt:
                        this.#proxy.state.control?.studio?.autoTrackingWorker[
                          toHexFromId(this.#workerId)! ?? ''
                        ]?.state?.status?.readyAt ?? Date.now()
                    }
                  },
                  ...(this.terminated ? { terminatedAt: +new Date() } : {})
                }
              }
            }
          }
        },
        {},
        false
      );
    } catch (e) {
      console.error('$GPU STATE SYNC ERROR', e);

      if (retry > 0) {
        setTimeout(() => {
          void this.#syncState(retry - 1);
        }, 250);

        return;
      }
    }

    setTimeout(this.verifyStateSyncComplete.bind(this), 1000);
  }

  async verifyStateSyncComplete() {
    if (this.terminated) {
      return;
    }

    const workerState =
      this.#proxy.state.control?.studio?.autoTrackingWorker[toHexFromId(this.#workerId)!];

    if (workerState?.state?.status?.status !== this.#status) {
      await this.#syncState();
      return;
    }

    return;
  }

  get status() {
    return this.#status;
  }

  get deviceId() {
    return this.#deviceId;
  }

  #startPingLoop() {
    clearTimeout(this.#pingLoop);

    this.#pingLoop = setInterval(() => {
      void this.#syncState();
    }, 30 * 1000);
  }

  #startPingListener() {
    const pingSubject = pingSubjectForWorker(toIdFromHex(this.#proxy.id)!, this.#workerId);

    if (this.#pingSubscription) {
      this.#pingSubscription.unsubscribe();
    }

    this.#pingSubscription = this.#proxy?.connection.subscribe(pingSubject, {
      callback: (_, message) => {
        if (message.data.toString() === new Uint8Array([0, 1, 2]).toString()) {
          this.#detector!.stopSubjectTracking();
          this.shutdown();

          return;
        }

        const replySubj = message.reply;

        if (!replySubj) {
          return;
        }

        const respData = new TextEncoder().encode('pong');

        (this.#proxy?.connection as NewtsConnection).publish(replySubj, respData);
      }
    });
  }

  reinit() {
    this.shutdown();
    this.#init();
  }

  #refreshTrackTimeout: NodeJS.Timeout | undefined;

  async refreshTrack() {
    clearTimeout(this.#refreshTrackTimeout);

    setTimeout(async () => {
      const deviceIdHex = toHexFromId<Device['id']>(this.#deviceId)!;

      const livekit = <LiveKitImpl>locateLiveKit(deviceIdHex)!;
      // @todo - this is a hack to get the video track for the camera
      // embedded link recovery: pass the embedded flag so auto-tracking sees a just-linked
      // embedded camera's track during the device→agent map's stale window
      // (the '0001' suffix is the embedded shadow-device convention, broker.ts:574).
      const track = livekit
        ?.videoForAgent(this.#agentId!, deviceIdHex.endsWith('0001'))
        .get(deviceIdHex);

      this.reinit();

      if (!track || !livekit) {
        console.warn('No track or livekit found for device', deviceIdHex, this.#agentId);
        return;
      }

      this.#status = Status.STATUS_STARTING;

      await this.#detector!.init(livekit, track);

      this.#status = Status.STATUS_RUNNING;

      this.#detector!.detectVideo(
        track,
        track.mediaStreamTrack,
        this.#subject,
        this.#agentId!,
        toHexFromId(this.#workerId)!,
        toHexFromId(this.#deviceId)!,
        this.#embeddingBeaconSubject!
      );

      void this.#syncState();
    }, 2500);
  }
}
